/**
 * TwilioManager - Handles Twilio Voice SDK integration
 * Manages device initialization, call handling, and event management
 */

import { get } from "svelte/store";
import { showToast } from "../../../../../assets/js/src/general/utils";
import { sendInboundCallResponse } from "../../../../../assets/js/src/general/websocket";
import { userStore } from "../../../../api-stores/user-store";
import {
  CALL_ACTIONS,
  endCallChecks,
  modifyCall,
} from "../../../../api/dialler";
import { ToastError } from "../../../../api/sw-toast-error";
import { attendeesState } from "../attendeesState.svelte";
import { diallerState } from "../diallerState.svelte";
import { diallerTokenStore } from "../stores";
import { closeLoadingScreen } from "./extension";

export class TwilioManager {
  device = null;
  activeCall = null;
  conversationId = null;
  token = null;
  isInitiator = null;
  isInitialized = false;
  isRefreshing = false;
  networkError = false;

  #eventHandlers = new Map();
  #deviceChangeHandler = null;
  #outcomeModalTimeout = null;

  /**
   * Set up a new Twilio Device
   */
  async #setupDevice(allowIncoming = false) {
    await diallerTokenStore.get({ allowIncoming });
    const tokenStore = get(diallerTokenStore);

    if (!tokenStore.token) {
      throw new Error("Failed to get Twilio token");
    }

    if (this.activeCall) {
      throw new Error("Call already in progress");
    }

    this.token = tokenStore.token;
    // If device is already initialized, only update the token
    if (this.device && this.isInitialized) {
      this.device.updateToken(this.token);
      return;
    }

    try {
      // eslint-disable-next-line no-undef
      if (typeof Twilio === "undefined" || !Twilio.Device) {
        throw new Error("Twilio SDK not loaded or incomplete");
      }

      const deviceOptions = {
        tokenRefreshMs: 60000,
      };

      const user = get(userStore);
      if (user?.diallerDebugger) {
        deviceOptions.logLevel = 1;
      }

      // eslint-disable-next-line no-undef
      this.device = new Twilio.Device(this.token, deviceOptions);

      this.setupEventHandlers();
      await this.setupAudioDevices();
      this.isInitialized = true;
    } catch (error) {
      diallerState.setWarning("network", "Failed to initialize voice service");
      showToast({
        title: "Voice service unavailable",
        subtitle: "Unable to initialize calling functionality",
        type: "danger",
      });
      throw error;
    }
  }

  /**
   * Set up Twilio Device event handlers
   */
  setupEventHandlers() {
    let tokenRefreshPromise = null;

    this.device.on("tokenWillExpire", async () => {
      if (tokenRefreshPromise) {
        return tokenRefreshPromise;
      }

      tokenRefreshPromise = (async () => {
        try {
          await diallerTokenStore.reload();
          const { token } = get(diallerTokenStore);
          if (token) {
            this.token = token;
            this.device.updateToken(token);
          }
        } catch (error) {
          diallerState.setWarning("network", "Failed to refresh voice token");
          throw error;
        } finally {
          tokenRefreshPromise = null;
        }
      })();

      return tokenRefreshPromise;
    });

    const errorHandler = (error) => {
      try {
        this.handleDeviceError(error);
      } catch {
        console.error("Error handling device error", error);
      }
    };

    this.device.on("error", errorHandler);
    this.#eventHandlers.set("device:error", {
      target: this.device,
      event: "error",
      handler: errorHandler,
    });

    const registeredHandler = () => {
      this.networkError = false;
      diallerState.clearWarning("network");
      this.#retrySendInboundCallResponse();
    };

    this.device.on("registered", registeredHandler);
    this.#eventHandlers.set("device:registered", {
      target: this.device,
      event: "registered",
      handler: registeredHandler,
    });

    if (this.device.audio) {
      this.#deviceChangeHandler = async () => {
        try {
          await this.updateAudioDevices();
        } catch (error) {
          console.error("Error updating audio devices", error);
        }
      };
      this.device.audio.on("deviceChange", this.#deviceChangeHandler);
      this.#eventHandlers.set("audio:deviceChange", {
        target: this.device.audio,
        event: "deviceChange",
        handler: this.#deviceChangeHandler,
      });
    }

    const incomingHandler = (inbound) => {
      this.activeCall = inbound;
      if (!attendeesState.candidate?.candidateId) {
        attendeesState.candidate = {
          ...(attendeesState.candidate | {}),
          phone: inbound.parameters.From,
        };
      }
      this.setupCallEventHandlers(inbound);
      diallerState.viewMode = "inbound-call";
      diallerState.isOpen = true;
      closeLoadingScreen();
    };

    this.device.on("incoming", incomingHandler);
    this.#eventHandlers.set("device:incoming", {
      target: this.device,
      event: "incoming",
      handler: incomingHandler,
    });
  }

  #retrySendInboundCallResponse(retries = 5) {
    if (this.conversationId && retries > 0) {
      //TODO remove setTimeout and handle by twilio driven event
      setTimeout(async () => {
        try {
          sendInboundCallResponse(this.conversationId, "ready");
          diallerState.isOpen = true;
        } catch {
          this.#retrySendInboundCallResponse(retries - 1);
        }
      }, 1000);
    } else {
      closeLoadingScreen();
    }
  }

  /**
   * Handle device errors
   * @param {Error} error - Twilio error object
   */
  handleDeviceError(error) {
    const errorMessage = error.message || "Unknown error";
    const errorCode = error.code;

    // Determine error category
    const isConnectionError = [31204, 31205, 31208].includes(errorCode);
    const isPermissionError = [31201, 31401].includes(errorCode);
    const isGeneralError = !isConnectionError && !isPermissionError;

    // Handle general errors with delay to let network errors show first
    if (isGeneralError) {
      if (this.networkError) {
        // Network error already shown, skip general error
        return;
      }

      // Delay general error by 2 seconds
      setTimeout(() => {
        if (this.networkError) {
          // Network error occurred during delay, skip
          return;
        }

        showToast({
          title: `Error ${errorCode}`,
          subtitle: errorMessage,
          type: "danger",
          id: "general",
          timeout: 15000,
        });
      }, 2000);
      return;
    }

    // Set network error flag for connection errors
    if (isConnectionError) {
      this.networkError = true;
    }

    switch (errorCode) {
      case 31205: // JWT token expired
        showToast({
          title: "Error 31205",
          subtitle: "Voice token expired. Please refresh.",
          type: "danger",
          id: "connection",
          timeout: 15000,
        });
        diallerState.setWarning(
          "network",
          "Voice token expired. Please refresh.",
        );
        break;
      case 31204: // Invalid JWT token
        showToast({
          title: "Error 31204",
          subtitle: "Invalid voice token. Please try again.",
          type: "danger",
          id: "connection",
          timeout: 15000,
        });
        diallerState.setWarning(
          "network",
          "Invalid voice token. Please try again.",
        );
        break;
      case 31208: // No network connection
        showToast({
          title: "Error 31208",
          subtitle: "No network connection",
          type: "danger",
          id: "connection",
          timeout: 15000,
        });
        diallerState.setWarning("network", "No network connection");
        break;
      case 31201:
      case 31401:
        this.#handleMicrophoneError(errorCode);
        break;
      default:
        diallerState.setWarning("network", `Voice error: ${errorMessage}`);
    }
  }

  #handleMicrophoneError(errorCode) {
    showToast({
      title: `Microphone Disabled (Error ${errorCode})`,
      subtitle:
        "Please enable microphone access in your browser settings and refresh the page",
      type: "danger",
      id: "permissions",
      timeout: 15000,
    });
    diallerState.setWarning("audio", "Microphone access not enabled");
    diallerState.microphonePermission = "denied";
  }

  /**
   * Set up audio device monitoring and selection
   */
  async setupAudioDevices() {
    if (!this.device || !this.device.audio) return;

    try {
      await this.updateAudioDevices();

      const { selectedInputDevice, selectedOutputDevice } = diallerState;

      if (selectedInputDevice) {
        await this.setInputDevice(selectedInputDevice);
      }

      if (selectedOutputDevice) {
        await this.setOutputDevice(selectedOutputDevice);
      }
    } catch (error) {
      console.error("Error setting up audio devices", error);
    }
  }

  /**
   * Update available audio devices
   */
  async updateAudioDevices() {
    if (!this.device || !this.device.audio) {
      throw new Error("Twilio Device not initialized");
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === "audioinput");
      const outputs = devices.filter((d) => d.kind === "audiooutput");

      diallerState.updateAvailableDevices({ inputs, outputs });
    } catch (error) {
      console.error("Error updating audio devices", error);
    }
  }

  /**
   * Set audio input device
   * @param {string} deviceId - Device ID
   */
  async setInputDevice(deviceId) {
    const previousDevice = diallerState.selectedInputDevice;
    diallerState.selectedInputDevice = deviceId;
    if (this.device?.audio) {
      try {
        await this.device.audio.setInputDevice(deviceId);
      } catch (error) {
        console.error("Error setting input device:", error);
        diallerState.selectedInputDevice = previousDevice;
        throw error;
      }
    }
  }

  /**
   * Set audio output device
   * @param {string} deviceId - Device ID
   */
  async setOutputDevice(deviceId) {
    const previousDevice = diallerState.selectedOutputDevice;
    diallerState.selectedOutputDevice = deviceId;
    if (this.device?.audio?.speakerDevices) {
      try {
        await this.device.audio.speakerDevices.set(deviceId);
      } catch (error) {
        console.error("Error setting output device:", error);
        diallerState.selectedOutputDevice = previousDevice;
        throw error;
      }
    }
  }

  /**
   * Set up an inbound call
   * @param {string} conversationId - Conversation ID
   */
  async setupInboundCall(conversationId) {
    if (this.activeCall) {
      sendInboundCallResponse(conversationId, "busy");
      return;
    }
    this.conversationId = conversationId;
    if (this.device) {
      // Device already initialized, send response
      sendInboundCallResponse(conversationId, "ready");
    } else {
      await this.#setupDevice(true);
      await this.device.register();
    }
  }

  /**
   * Common method for initiating calls and conference joins
   * @param {Object} params - Connection parameters
   * @param {boolean} isInbound - Whether this is an inbound call
   * @returns {Promise<Object>} Call object
   */
  async #connectCall(params, isInbound = false) {
    await this.#setupDevice(isInbound);
    this.isInitiator = !params.conferenceId && !params.mode;

    try {
      this.activeCall = await this.device.connect({
        params: {
          ...params,
          origin: "web-app",
          token: this.token,
        },
      });

      this.setupCallEventHandlers(this.activeCall);

      diallerState.activeCall = this.activeCall;
      diallerState.startCall();

      return this.activeCall;
    } catch (error) {
      this.activeCall = null;
      diallerState.activeCall = null;

      diallerState.setWarning("network", "Failed to connect call");

      if (error instanceof ToastError) {
        showToast(error);
      } else {
        showToast({
          title: "Call failed",
          subtitle: error.message || "Unable to connect call",
          type: "danger",
        });
      }
      throw error;
    }
  }

  /**
   * Make an outbound call
   * @param {Object} params - Call parameters
   * @param {string} params.phoneNumber - Phone number to call
   * @param {string} params.todoId - Associated todo ID
   * @param {string} params.candidateId - Associated candidate ID
   * @returns {Promise<Object>} Call object
   */
  async makeCall({ phoneNumber, todoId, candidateId }) {
    if (!phoneNumber) {
      throw new Error("Phone number is required");
    }

    diallerState.callDirection = "outbound";

    return this.#connectCall({
      To: phoneNumber,
      toDoId: todoId,
      candidateId: candidateId,
    });
  }

  /**
   * Join an ongoing conference call
   * @param {Object} params - Conference parameters
   * @param {string} params.conferenceId - Conference ID to join
   * @param {string} params.mode - Mode to join the conference
   * @returns {Promise<Object>} Call object
   */
  async joinConference({ conferenceId, mode }) {
    if (!conferenceId) throw new Error("Conference ID is required");

    return this.#connectCall({ conferenceId: conferenceId, mode: mode });
  }

  async acceptInboundCall(params) {
    return this.#connectCall(params, true);
  }

  /**
   * Set up event handlers for active call
   * @param {Object} call - Twilio Call object
   */
  setupCallEventHandlers(call) {
    if (!call) {
      return;
    }

    call.on("accept", async () => {
      const callId = call.parameters.CallSid;
      diallerState.callId = callId;
      diallerState.clearWarning("network");
      diallerState.timer.startDurationTimer();

      let user = get(userStore);
      if (!user?.userId) {
        await userStore.get();
        user = get(userStore);
      }
      if (user?.diallerDefaultRecordingOn) {
        diallerState.callHasRecording = true;
        diallerState.isRecording = true;
      }
    });

    call.on("cancel", () => {
      this.handleCallEnded();
    });

    call.on("reject", () => {
      this.activeCall = null;
      diallerState.activeCall = null;
    });

    call.on("disconnect", () => {
      this.handleCallEnded();
    });

    call.on("error", (error) => {
      this.handleDeviceError(error);
    });

    call.on("warning", (warningName) => {
      this.handleCallWarning(warningName, false);
    });

    call.on("warning-cleared", (warningName) => {
      this.handleCallWarning(warningName, true);
    });

    call.on("mute", (isMuted) => {
      diallerState.isMuted = isMuted;
    });
  }

  /**
   * Handle call warnings
   * @param {string} warningName - Warning type
   * @param {boolean} cleared - Whether warning is cleared
   */
  handleCallWarning(warningName, cleared) {
    const warningMessages = {
      "low-mos": "Poor call quality detected",
      "high-rtt": "High network latency detected",
      "high-packet-loss": "Network packet loss detected",
      "high-jitter": "Network instability detected",
      "constant-audio-input-level": "No microphone audio detected",
      "constant-audio-output-level": "No speaker audio detected",
    };

    const audioWarnings = [
      "constant-audio-input-level",
      "constant-audio-output-level",
    ];
    const warningType = audioWarnings.includes(warningName)
      ? "audio"
      : "network";

    if (cleared) {
      diallerState.clearWarning(warningType);
    } else {
      const message = warningMessages[warningName] || "Network issue detected";
      diallerState.setWarning(warningType, message);
    }
  }

  /**
   * Handle call ended
   */
  async handleCallEnded() {
    if (this.#outcomeModalTimeout) {
      clearTimeout(this.#outcomeModalTimeout);
      this.#outcomeModalTimeout = null;
    }

    const callId = this.activeCall?.parameters?.CallSid;

    diallerState.endCall();
    this.activeCall = null;
    diallerState.activeCall = null;

    // Early exit if no call ID, todo ID is active, or is logged
    if (
      !this.isInitialized ||
      !callId ||
      diallerState.todoId ||
      diallerState.isLogged
    ) {
      diallerState.closeDialler();
      return;
    }

    // Check if outcome modal should be shown
    this.#outcomeModalTimeout = setTimeout(async () => {
      try {
        // Check if call exists on backend
        const callData = await endCallChecks({
          callId: callId,
          direction: diallerState.callDirection,
        });

        if (callData && Object.keys(callData).length > 0) {
          diallerState.viewMode = "outcome";
        } else {
          diallerState.closeDialler();
        }
      } catch {
        diallerState.closeDialler();
      }
    }, 500);
  }

  /**
   * Toggle mute state
   */
  async toggleMute() {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    if (!this.isInitialized) {
      throw new Error("Twilio Device not initialized");
    }

    await this.activeCall.mute(!diallerState.isMuted);
  }

  /**
   * Start call recording
   */
  async startRecording() {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    const callId = diallerState.callId || this.activeCall.parameters?.CallSid;
    if (!callId) {
      throw new Error("No call ID available for recording");
    }

    if (this.activeCall.status() === "closed") {
      throw new Error("Call has already ended");
    }

    try {
      const response = await modifyCall({
        callSid: callId,
        action: CALL_ACTIONS.START_RECORDING,
        direction: diallerState.callDirection,
      });
      // Check if response has recordingStatus
      if (response?.recordingStatus) {
        if (response.recordingStatus === "in-progress") {
          diallerState.isRecording = true;
          diallerState.callHasRecording = true;
        }
      } else {
        // If no recordingStatus in response, assume success (like legacy)
        diallerState.isRecording = true;
        diallerState.callHasRecording = true;
      }
    } catch (error) {
      showToast(error);
      throw error;
    }
  }

  /**
   * Pause call recording
   */
  async pauseRecording() {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    const callId = diallerState.callId || this.activeCall.parameters?.CallSid;
    if (!callId) {
      throw new Error("No call ID available");
    }

    if (this.activeCall.status() === "closed") {
      throw new Error("Call has already ended");
    }

    try {
      await modifyCall({
        callSid: callId,
        action: CALL_ACTIONS.PAUSE_RECORDING,
        direction: diallerState.callDirection,
      });
      diallerState.isRecording = false;
    } catch (error) {
      showToast(error);
      throw error;
    }
  }

  /**
   * Resume call recording
   */
  async resumeRecording() {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    const callId = diallerState.callId || this.activeCall.parameters?.CallSid;
    if (!callId) {
      throw new Error("No call ID available");
    }

    if (this.activeCall.status() === "closed") {
      throw new Error("Call has already ended");
    }

    try {
      await modifyCall({
        callSid: callId,
        action: CALL_ACTIONS.RESUME_RECORDING,
        direction: diallerState.callDirection,
      });
      diallerState.isRecording = true;
      diallerState.callHasRecording = true;
    } catch (error) {
      showToast(error);
      throw error;
    }
  }

  /**
   * Stop call recording
   */
  async stopRecording() {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    const callId = diallerState.callId || this.activeCall.parameters?.CallSid;
    if (!callId) {
      throw new Error("No call ID available");
    }

    if (this.activeCall.status() === "closed") {
      throw new Error("Call has already ended");
    }

    try {
      await modifyCall({
        callSid: callId,
        action: CALL_ACTIONS.STOP_RECORDING,
        direction: diallerState.callDirection,
      });
      diallerState.isRecording = false;
    } catch (error) {
      showToast(error);
      throw error;
    }
  }

  /**
   * Send DTMF tone
   * @param {string} digit - Digit to send (0-9, *, #)
   */
  sendDigit(digit) {
    if (!this.activeCall) {
      throw new Error("No active call");
    }

    if (!this.isInitialized) {
      throw new Error("Twilio Device not initialized");
    }

    this.activeCall.sendDigits(digit);
  }

  /**
   * Clean up and destroy Twilio Device
   * Also aliased as cleanup() for consistency
   */
  async destroy() {
    try {
      this.initializationPromise = null;

      if (this.#outcomeModalTimeout) {
        clearTimeout(this.#outcomeModalTimeout);
        this.#outcomeModalTimeout = null;
      }

      if (this.activeCall) {
        await this.activeCall.disconnect();
      }

      for (const [, { target, event, handler }] of this.#eventHandlers) {
        try {
          if (target && typeof target.off === "function") {
            target.off(event, handler);
          } else if (target && typeof target.removeListener === "function") {
            target.removeListener(event, handler);
          }
        } catch {
          // Error removing event listener
        }
      }
      this.#eventHandlers.clear();

      if (this.device) {
        this.device.destroy();
        this.device = null;
      }

      this.#deviceChangeHandler = null;
      this.token = null;
      this.isInitialized = false;
      this.isRefreshing = false;
      this.networkError = false;
    } catch (error) {
      console.error("Error destroying TwilioManager:", error);
    }
  }

  /**
   * Alias for destroy() to match diallerState's expectations
   */
  async cleanup() {
    return this.destroy();
  }
}

export const twilioManager = new TwilioManager();
